import { POLLS_API_PREFIX } from '@/config/polls'

/**
 * B-side funnel-events для `POST /api/max/polls/events` (contract-doc §2.6).
 * Whitelist Phase 1 — всё что коррелирует с `refSource='polls'` для BI-воронки.
 * Не в whitelist → B возвращает `400 INVALID_EVENT_NAME` (мы игнорим — fail-open).
 *
 * Правила дедупа с B (согласовано 2026-07-27):
 * - `poll_first_published` — только B авто-emit'ит (F не шлёт, иначе двойной счёт)
 * - `poll_channel_connected` — только F LK (B не эмитит; воронка делается SQL-JOIN'ом)
 * - `poll_admin_analytics_teaser_*` — оба (webview + LK); F шлёт с `meta.source='lk'`
 * - Все остальные — F шлёт единожды в B `/events`
 */
export type PollEventName =
  // webview-only (F не шлёт из ЛК)
  | 'poll_opened'
  | 'poll_voted'
  | 'poll_signature_shown'
  | 'poll_signature_clicked'
  | 'poll_signature_suppressed'
  // dual-source (webview + LK): F шлёт со своими meta.source='lk'
  | 'poll_admin_analytics_teaser_shown'
  | 'poll_admin_analytics_teaser_cta_click'
  // webview-only: тап CTA «Аналитика в ЛК» в миниаппе (B whitelist 2026-07-28)
  | 'poll_admin_lk_cta_click'
  // F-only (LK) — onboarding-воронка
  | 'poll_onboarding_opened'
  | 'poll_onboarding_step_completed'
  | 'poll_channel_connected'

export interface EmitPollEventOptions {
  /** UUID poll'а; null/undefined когда контекст события не привязан к конкретному опросу. */
  pollId?: string | null
  /** Any JSON-serializable meta; должен уместиться в 4KB (иначе 400 META_TOO_LARGE). */
  meta?: Record<string, unknown>
}

/**
 * Fire-and-forget POST в B-side `/events`. Использует `keepalive:true`,
 * чтобы событие долетело даже если юзер закрыл вкладку (§5.4 контракт).
 * Ошибки глотаем — не тормозим UX на event-логировании.
 *
 * НЕ шлём в BetterStack параллельно — вызывающий сам двойным вызовом
 * `track()` + `emitPollEvent()` пишет в оба стрима когда нужен и funnel, и UI-analytics.
 */
export function emitPollEvent(name: PollEventName, opts: EmitPollEventOptions = {}): void {
  const body: Record<string, unknown> = { name, meta: opts.meta ?? {} }
  if (opts.pollId) body.pollId = opts.pollId
  try {
    void fetch(`${POLLS_API_PREFIX}/events`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {})
  } catch {
    // ignore
  }
}
